import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import AppLoader from '../../../../Components/AppLoader';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { useParams } from 'react-router';
import { useStyle as onboardingStyle } from '../../../CustomerOnBoarding';
import { checkValidEmail, notify } from 'utils';
import { UserServices } from '../../../../Services/UserServices';
import { ERROR_MESSAGE } from '../../../../utils/constants';
import { formatApiErrorMessage } from '../../../../utils/userProfileFields';
import PhoneInputField from '../../../../Components/PhoneInputField';

const userServices = new UserServices();
let nextAdministratorRowId = 0;

const useStyles = makeStyles((theme) => ({
    formContainer: {
        marginBottom: theme.spacing(4),
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    sectionTitle: {
        color: '#263548',
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.4,
    },
    sectionDescription: {
        color: '#718096',
        fontSize: '0.75rem',
        lineHeight: 1.5,
        marginTop: theme.spacing(0.5),
    },
    addButton: {
        flexShrink: 0,
        minWidth: 190,
        textTransform: 'none',
        fontWeight: 600,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    administratorSection: {
        padding: theme.spacing(2.5, 0),
        borderBottom: '1px solid #e4e9ef',
        '&:first-of-type': {
            borderTop: '1px solid #e4e9ef',
        },
    },
    administratorHeader: {
        minHeight: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    administratorTitle: {
        color: '#263548',
        fontSize: '0.875rem',
        fontWeight: 700,
    },
    administratorStatus: {
        color: '#7a8797',
        fontSize: '0.6875rem',
        fontWeight: 500,
        marginTop: 2,
    },
    removeButton: {
        color: '#d14343',
    },
    sectionLoaderWrap: {
        minHeight: 280,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonProgress: {
        color: theme.palette.common.white,
    },
}));

export const initFormState = {
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    location: '',
    contact_no: '',
    job_title: '',
    role: 'customeradmin',
    is_active: true,
};

const createAdministrator = (administrator = {}) => ({
    ...initFormState,
    ...administrator,
    firstName: administrator.firstName || '',
    lastName: administrator.lastName || '',
    email: administrator.email || '',
    userName: administrator.userName || administrator.email || '',
    location: administrator.location || '',
    contact_no: administrator.contact_no || administrator.mobile_number || '',
    job_title: administrator.job_title || '',
    role: 'customeradmin',
    is_active: administrator.is_active !== false,
    _rowId: administrator.uuid || `new-administrator-${++nextAdministratorRowId}`,
});

const getAdministratorsFromBackup = (formBackupState) => {
    if (Array.isArray(formBackupState)) return formBackupState;
    if (Array.isArray(formBackupState?.administrators)) return formBackupState.administrators;
    return [];
};

const CustomerAdmin = ({
    activeStep,
    handleBack,
    handleNext,
    formBackupState,
    handleUpdateBackupFormData,
}) => {
    const classes = useStyles();
    const onboardingClasses = onboardingStyle();
    const { id } = useParams();
    const customerId = id || localStorage.getItem('customerUUID');

    const [administrators, setAdministrators] = React.useState([createAdministrator()]);
    const [removedIds, setRemovedIds] = React.useState([]);
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);

    React.useEffect(() => {
        let active = true;

        const loadAdministrators = async () => {
            if (!customerId) {
                const backupAdministrators = getAdministratorsFromBackup(formBackupState);
                if (active && backupAdministrators.length) {
                    setAdministrators(backupAdministrators.map(createAdministrator));
                }
                if (active) setInitialLoading(false);
                return;
            }

            const result = await userServices.customerAdminUser(customerId, 'customeradmin');
            if (!active) return;

            if (result && result.status && Array.isArray(result?.data?.results)) {
                const savedAdministrators = result.data.results.map(createAdministrator);
                setAdministrators(savedAdministrators.length ? savedAdministrators : [createAdministrator()]);
            } else {
                const backupAdministrators = getAdministratorsFromBackup(formBackupState);
                if (backupAdministrators.length) {
                    setAdministrators(backupAdministrators.map(createAdministrator));
                }
                notify('error', formatApiErrorMessage(result, 'Customer administrators could not be loaded.'));
            }
            setInitialLoading(false);
        };

        loadAdministrators();
        return () => {
            active = false;
        };
    }, [customerId, formBackupState]);

    const updateAdministrator = (rowId, field, value) => {
        setAdministrators(current => current.map(administrator => {
            if (administrator._rowId !== rowId) return administrator;
            if (field === 'email') {
                return {
                    ...administrator,
                    email: value,
                    userName: administrator.uuid ? administrator.userName : value,
                };
            }
            return { ...administrator, [field]: value };
        }));
    };

    const addAdministrator = () => {
        setAdministrators(current => [...current, createAdministrator()]);
    };

    const removeAdministrator = (rowId) => {
        if (administrators.length === 1) return;
        const administrator = administrators.find(item => item._rowId === rowId);
        if (administrator?.uuid) {
            setRemovedIds(ids => ids.includes(administrator.uuid)
                ? ids
                : [...ids, administrator.uuid]);
        }
        setAdministrators(current => current.filter(item => item._rowId !== rowId));
    };

    const validateAdministrators = () => {
        if (!administrators.length || !administrators.some(administrator => administrator.is_active !== false)) {
            notify('error', 'At least one active customer administrator is required.');
            return false;
        }

        for (let index = 0; index < administrators.length; index += 1) {
            const administrator = administrators[index];
            if (
                !administrator.firstName?.trim()
                || !administrator.lastName?.trim()
                || !administrator.email?.trim()
                || !administrator.location?.trim()
            ) {
                notify('error', `Complete all required fields for Administrator ${index + 1}.`);
                return false;
            }
            if (!checkValidEmail(administrator.email.trim())) {
                notify('error', `Administrator ${index + 1}: ${ERROR_MESSAGE.VALID_EMAIL}`);
                return false;
            }
        }

        const emails = administrators.map(administrator => administrator.email.trim().toLowerCase());
        if (new Set(emails).size !== emails.length) {
            notify('error', 'Each customer administrator must use a unique email address.');
            return false;
        }

        return true;
    };

    const buildPayload = () => ({
        administrators: administrators.map((administrator) => {
            const payload = {
                firstName: administrator.firstName.trim(),
                lastName: administrator.lastName.trim(),
                email: administrator.email.trim(),
                userName: (administrator.uuid
                    ? administrator.userName || administrator.email
                    : administrator.email).trim(),
                location: administrator.location.trim(),
                contact_no: administrator.contact_no || '',
                job_title: administrator.job_title?.trim() || '',
                role: 'customeradmin',
                is_active: administrator.is_active !== false,
            };
            if (administrator.uuid) payload.uuid = administrator.uuid;
            return payload;
        }),
        removed_ids: removedIds,
    });

    const handleFormSubmit = async () => {
        if (!validateAdministrators()) return;
        if (!customerId) {
            notify('error', 'Save the customer before adding administrators.');
            return;
        }

        setSubmitLoading(true);
        const result = await userServices.saveCustomerAdminUsers(customerId, buildPayload());
        if (result && result.status && Array.isArray(result?.data?.results)) {
            const savedAdministrators = result.data.results.map(createAdministrator);
            setAdministrators(savedAdministrators.length ? savedAdministrators : [createAdministrator()]);
            setRemovedIds([]);
            handleUpdateBackupFormData(
                { administrators: savedAdministrators, removed_ids: [] },
                'customerAdmin'
            );
            notify('success', result.message || 'Customer administrators saved successfully.');
            setSubmitLoading(false);
            handleNext();
            return;
        }

        notify('error', formatApiErrorMessage(result, 'Customer administrators could not be saved.'));
        setSubmitLoading(false);
    };

    return (
        <>
            {initialLoading ? (
                <AppLoader minHeight={280} />
            ) : (
                <div className={classes.formContainer}>
                    <div className={classes.sectionHeader}>
                        <div>
                            <Typography component="h2" className={classes.sectionTitle}>
                                Customer administrators
                            </Typography>
                            <Typography className={classes.sectionDescription}>
                                Add the people responsible for managing this customer account.
                            </Typography>
                        </div>
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={addAdministrator}
                            className={classes.addButton}
                        >
                            Add another administrator
                        </Button>
                    </div>

                    {administrators.map((administrator, index) => (
                        <section className={classes.administratorSection} key={administrator._rowId}>
                            <div className={classes.administratorHeader}>
                                <div>
                                    <Typography component="h3" className={classes.administratorTitle}>
                                        Administrator {index + 1}
                                    </Typography>
                                    <Typography className={classes.administratorStatus}>
                                        {administrator.uuid ? 'Existing customer administrator' : 'New customer administrator'}
                                    </Typography>
                                </div>
                                {administrators.length > 1 && (
                                    <Tooltip title="Remove administrator">
                                        <IconButton
                                            type="button"
                                            aria-label={`Remove Administrator ${index + 1}`}
                                            onClick={() => removeAdministrator(administrator._rowId)}
                                            className={classes.removeButton}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label="First Name *"
                                        name="firstName"
                                        value={administrator.firstName}
                                        onChange={(event) => updateAdministrator(
                                            administrator._rowId,
                                            'firstName',
                                            event.target.value
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label="Surname *"
                                        name="lastName"
                                        value={administrator.lastName}
                                        onChange={(event) => updateAdministrator(
                                            administrator._rowId,
                                            'lastName',
                                            event.target.value
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label="Work Email *"
                                        name="email"
                                        type="email"
                                        value={administrator.email}
                                        disabled={!!administrator.uuid}
                                        helperText={administrator.uuid ? 'Email cannot be changed after creation.' : 'Used as the username.'}
                                        onChange={(event) => updateAdministrator(
                                            administrator._rowId,
                                            'email',
                                            event.target.value
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label="Location *"
                                        name="location"
                                        value={administrator.location}
                                        onChange={(event) => updateAdministrator(
                                            administrator._rowId,
                                            'location',
                                            event.target.value
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PhoneInputField
                                        label="Mobile Number"
                                        required={false}
                                        value={String(administrator.contact_no || '').replace(/^\+/, '')}
                                        handleChange={(value) => updateAdministrator(
                                            administrator._rowId,
                                            'contact_no',
                                            value ? `+${String(value).replace(/^\+/, '')}` : ''
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label="Job Title (optional)"
                                        name="job_title"
                                        value={administrator.job_title}
                                        onChange={(event) => updateAdministrator(
                                            administrator._rowId,
                                            'job_title',
                                            event.target.value
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </section>
                    ))}
                </div>
            )}

            <div className={onboardingClasses.buttons}>
                <div className={onboardingClasses.wrapper}>
                    {activeStep !== 0 && (
                        <Button
                            type="button"
                            onClick={handleBack}
                                variant="outlined"
                                color="default"
                            className={onboardingClasses.cancelBtn}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={handleFormSubmit}
                        variant="contained"
                        color="primary"
                        disabled={submitLoading || initialLoading}
                        className={onboardingClasses.button}
                    >
                        {submitLoading ? (
                            <CircularProgress size={18} className={classes.buttonProgress} />
                        ) : 'Next'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default CustomerAdmin;
